import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Button,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Radio,
    Select,
    Tooltip,
    Typography
} from "@mui/material/";
import { Close } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import actions from "../../../../Store/actions";
import {
    useAssetTypeIdentifiers,
    useAssetTypeResolutionReasons,
    useAssetTypeValidationRules
} from "../../../../common/hooks";
import { strings } from "../../../../common/i18n";
import { enums, helpers } from "../../../../common/lib";
import { SuccessThemeProvider } from "../../../../common/styles/providers";
import Paper from "../Paper";
import styles from "./styles";
import { logToSentry } from "../../../../config/Sentry";

type Props = {
    assetMatch: any,
    onClose: Function,
    onShowAssetMatch: Function,
    onSubmit: Function,
    value: any
};

/**
 * Alt-asset Resolve Component
 */
export default ({
    assetMatch,
    onClose,
    onShowAssetMatch,
    onSubmit,
    value
}: Props) => {
    const { assets, auditLocations, auth, locations, typesList } = useSelector(
        (state) => state
    );
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    const identifiers = useAssetTypeIdentifiers(value.type);
    const [loading, setLoading] = useState(false);
    const [location, setLocation] = useState(null);
    const [resolutionReason, setResolutionReason] = useState(null);
    const resolutionReasons = useAssetTypeResolutionReasons(value.type);
    // Hoisted out of vinCheck() below — must run unconditionally on every
    // render, not just when value.type isn't already a string, or this
    // violates the Rules of Hooks.
    const assetTypeValidationRules = useAssetTypeValidationRules(value.type);

    useEffect(() => {
        if (location === null && value && value.assetAuxiliary) {
            const next = helpers.getListItemForId(assets, value.assetAuxiliary);
            setLocation(next.location);
        }
        if (
            resolutionReason === null &&
            value &&
            value.altAssetResolutions.length
        ) {
            const next =
                value.altAssetResolutions[value.altAssetResolutions.length - 1];
            setResolutionReason(next.resolutionReason);
        }
    }, [value]);

    useEffect(() => {
        if (location === null) {
            if (auditLocations.data.length === 1 && locations.data.length) {
                const next = helpers.getListItemForId(
                    locations,
                    auditLocations.data[0].location
                );
                setLocation(next.id);
            }
        }
    }, [auditLocations, location, locations]);

    const assetReject = useMemo(() => {
        const next = helpers.getListItemForId(
            typesList.altAssetResolutionTypes,
            enums.types.altAssetResolution.rejectAsset
        );
        return next.name;
    }, [typesList]);

    /** Check for make type unknown */
    const makeUnknown = useMemo(() => {
        const make = helpers.getListItemForId(typesList.makes, value.make);
        return make && make.name && make.name.toLowerCase() === "unknown"
            ? true
            : false;
    }, [typesList, value]);

    /** Check for make type unknown */
    const typeUnknown = useMemo(() => {
        const type = helpers.getListItemForId(typesList.assetTypes, value.type);
        return type && type.name && type.name.toLowerCase() === "unknown"
            ? true
            : false;
    }, [typesList, value]);

    const submitDisabled = useMemo(() => {
        if (makeUnknown) return true;
        if (value.isResolved) {
            return true;
        }
        if (!(resolutionReason && location)) {
            return true;
        }
        if (!Boolean(assetMatch)) {
            const { rejectAsset } = enums.types.altAssetResolution;
            if (!value.identifier1 && resolutionReason !== rejectAsset) {
                return true;
            }
        }
        return false;
    }, [location, resolutionReason, value, makeUnknown]);

    /** Validating Valid Vin */
    const vinCheck = () => {
        const { identifier1 } = value;
        const { rejectAsset } = enums.types.altAssetResolution;

        /** If not rejecting validate VIN */
        if (identifier1 && identifier1.length >= 7 && identifier1.length < 18) {
            const name = Object.keys({ identifier1 })[0];

            const rules =
                typeof value.type === "string"
                    ? value.type
                    : assetTypeValidationRules;

            const { data } = typesList.invalidIdentifiers;
            const invalidKey = data.find(
                (item) => item.name.toUpperCase() === identifier1.toUpperCase()
            );

            const regex = helpers.regex.parseString(rules[name].regex);
            // If there are no rules then it is unknown
            if (!regex) {
                // Check for the basic vin check
                const reg = helpers.regex.parseString("/^[0-9A-HJ-NPR-Z]+$/i");
                return reg.test(identifier1);
            }

            if (regex && regex.test(identifier1) && invalidKey === undefined) {
                return true;
            }
        }
        /**  IF rejecting the asset  */
        if (resolutionReason === rejectAsset) return true;
        return false;
    };

    const handleListItemClick = (next: number) => () =>
        setResolutionReason(next);

    const listItemDisabled = (item: any) => {
        const { rejectAsset } = enums.types.altAssetResolution;
        return (
            value.isResolved || (!value.identifier1 && item.id !== rejectAsset)
        );
    };

    const onLocationChange = (event: Event) => setLocation(event.target.value);

    const onResolve = async () => {
        try {
            const { asset, auxiliary } = assetMatch || {};
            setLoading(true);
            await dispatch(
                actions.altAssets.create.bulkUpdatePostRequest([
                    {
                        id: value.id,
                        asset: asset ? asset.id : null,
                        assetAuxiliary: auxiliary ? auxiliary.id : null,
                        decisionDate: new Date().toISOString(),
                        decisionUser: auth.account.id,
                        location,
                        resolutionReason
                    }
                ])
            );
            dispatch(actions.toast.create.success(strings("assetResolved")));
            setLoading(false);
            onSubmit && onSubmit();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On resolving asset:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onResolveAsset"))
            );
        }
    };

    return (
        <Paper
            HeaderComponent={
                <>
                    <Grid alignItems="center" container direction="row">
                        <Typography gutterBottom variant="h5">
                            {strings("resolve")}
                        </Typography>
                        <div className={classes.flexGrow} />
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    </Grid>
                    <Divider />
                    <div className={classes.spacer} />
                    <FormControl fullWidth>
                        <InputLabel>{strings("location")}</InputLabel>
                        <Select
                            disabled={value.isResolved}
                            onChange={onLocationChange}
                            required
                            value={location || ""}
                        >
                            {auditLocations.data.map((item) => {
                                const next = helpers.getListItemForId(
                                    locations,
                                    item.location
                                );
                                return (
                                    <MenuItem
                                        key={next.id}
                                        value={item.location}
                                    >
                                        {next.name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    {!value.identifier1 && (
                        <Alert severity="warning">
                            {strings("messages.selectLocation", {
                                name: identifiers.identifier1,
                                resolution: assetReject
                            })}
                        </Alert>
                    )}
                    {location === null ? (
                        <Alert severity="error">
                            {strings("messages.selectLocation")}
                        </Alert>
                    ) : null}
                </>
            }
            BodyComponent={
                <List>
                    {resolutionReasons.map((item) => (
                        <Tooltip key={item.id} title={item.description}>
                            <ListItem
                                button
                                disabled={listItemDisabled(item)}
                                onClick={handleListItemClick(item.id)}
                                selected={item.id === resolutionReason}
                            >
                                <Radio
                                    disabled={listItemDisabled(item)}
                                    onClick={handleListItemClick(item.id)}
                                    checked={item.id === resolutionReason}
                                    value={item.name}
                                />
                                <ListItemText primary={item.name} />
                            </ListItem>
                        </Tooltip>
                    ))}
                </List>
            }
            FooterComponent={
                <>
                    {Boolean(assetMatch) && (
                        <>
                            <Divider />
                            <div className={classes.spacer} />
                            <Button
                                color="primary"
                                fullWidth
                                onClick={onShowAssetMatch}
                                size="large"
                            >
                                {strings("matchAccepted")}
                            </Button>
                            <div className={classes.spacer} />
                        </>
                    )}
                    <Divider />
                    <div className={classes.spacer} />

                    {!vinCheck() && (
                        <Alert severity="error">
                            {strings("errors.identifier1Valid", {
                                name: identifiers.identifier1,
                                resolution: assetReject
                            })}
                        </Alert>
                    )}

                    {typeUnknown && (
                        <Alert severity="error">
                            {strings("errors.typeUnknown")}
                        </Alert>
                    )}

                    {makeUnknown && (
                        <Alert severity="error">
                            {strings("errors.makeUnknown")}
                        </Alert>
                    )}

                    <div className={classes.buttonContainer}>
                        <Button
                            className={classes.buttonStyle}
                            color="error"
                            onClick={onClose}
                            size="large"
                            variant="outlined"
                        >
                            {strings("cancel")}
                        </Button>
                        <SuccessThemeProvider>
                            <Button
                                className={classes.buttonStyle}
                                color="primary"
                                disabled={
                                    !vinCheck() ||
                                    (loading && submitDisabled) ||
                                    submitDisabled
                                }
                                fullWidth
                                onClick={onResolve}
                                size="large"
                                variant="contained"
                            >
                                {strings(
                                    value.isResolved
                                        ? "resolved"
                                        : "resolveAsset"
                                )}
                            </Button>
                        </SuccessThemeProvider>
                    </div>
                </>
            }
        />
    );
};
